import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  Download,
  FileSignature,
  Info,
  Loader2,
  RefreshCw,
  Eye,
  Save,
  Send,
  X,
} from "lucide-react";
import { toast } from "sonner";
import DashboardLayout from "@/components/DashboardLayout";
import ContractSignaturePanel from "@/components/contracts/ContractSignaturePanel";
import { CONTRACT_STATE_LABELS } from "@/pages/admin/AdminContracts";
import { api, formatApiError } from "@/lib/api";
import { SignedLink } from "@/lib/signedFile";
import { useAuth } from "@/context/AuthContext";
import { appConfirm } from "@/lib/appDialog";

const KIND_LABEL = {
  ASSOCIATION_AGREEMENT: "اتفاقية جمعية",
  STUDENT_COMMITMENT: "تعهد طالب",
};

const STATE_TONES = {
  DRAFT: "bg-violet-50 text-violet-700 border-violet-200",
  ACTIVE: "bg-emerald-50 text-emerald-700 border-emerald-200",
  SIGNED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  PENDING_SIGNATURES: "bg-amber-50 text-amber-800 border-amber-200",
  PARTIALLY_SIGNED: "bg-amber-50 text-amber-800 border-amber-200",
  AWAITING_STUDENT_SIGNATURE: "bg-amber-50 text-amber-800 border-amber-200",
  FINALIZING: "bg-sky-50 text-sky-700 border-sky-200",
  FINALIZATION_FAILED: "bg-rose-50 text-rose-700 border-rose-200",
  EXPIRED: "bg-slate-100 text-slate-600 border-slate-200",
  TERMINATED: "bg-slate-100 text-slate-600 border-slate-200",
  SUPERSEDED: "bg-slate-100 text-slate-600 border-slate-200",
  VOID: "bg-slate-100 text-slate-500 border-slate-200",
};

const CURRENT_STATES = new Set([
  "DRAFT",
  "PENDING_SIGNATURES",
  "PARTIALLY_SIGNED",
  "FINALIZING",
  "FINALIZATION_FAILED",
  "ACTIVE",
  "AWAITING_STUDENT_SIGNATURE",
  "SIGNED",
]);

function StateBadge({ state }) {
  const tone = STATE_TONES[state] || "bg-slate-100 text-slate-600 border-slate-200";
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${tone}`}>
      {CONTRACT_STATE_LABELS[state] || state || "—"}
    </span>
  );
}

function formatShortDate(value) {
  if (!value) return "—";
  return new Intl.DateTimeFormat("ar-SA", { dateStyle: "medium" }).format(new Date(value));
}

function formatDateTime(value) {
  if (!value) return "—";
  return new Intl.DateTimeFormat("ar-SA", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

export default function AssociationContracts() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/contracts/documents");
      setDocuments(data.documents || []);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Auto-open document from ?document= query string
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const doc = params.get("document");
    if (doc) setOpenId(doc);
  }, [location.search]);

  const closeDrawer = useCallback(() => {
    setOpenId(null);
    const params = new URLSearchParams(location.search);
    if (params.get("document")) {
      params.delete("document");
      navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  const stats = useMemo(() => {
    let mySign = 0;
    let waitingSiaq = 0;
    let active = 0;
    let closed = 0;
    for (const doc of documents) {
      if (doc.kind !== "ASSOCIATION_AGREEMENT") continue;
      if (doc.state === "DRAFT" || doc.state === "PENDING_SIGNATURES") mySign += 1;
      else if (doc.state === "PARTIALLY_SIGNED") waitingSiaq += 1;
      else if (doc.state === "FINALIZING") waitingSiaq += 1;
      else if (doc.state === "ACTIVE") active += 1;
      else if (["EXPIRED", "TERMINATED", "SUPERSEDED", "VOID", "FINALIZATION_FAILED"].includes(doc.state)) closed += 1;
    }
    return { mySign, waitingSiaq, active, closed };
  }, [documents]);

  const { current, history } = useMemo(() => {
    const aaDocs = documents.filter((d) => d.kind === "ASSOCIATION_AGREEMENT");
    const cur = aaDocs.filter((d) => CURRENT_STATES.has(d.state));
    const hist = aaDocs.filter((d) => !CURRENT_STATES.has(d.state));
    return { current: cur, history: hist };
  }, [documents]);

  return (
    <DashboardLayout>
      <div data-testid="association-contracts" dir="rtl">
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-teal-700">
              عقود الجمعية
            </div>
            <h1 className="mt-2 text-2xl font-heading font-extrabold text-slate-900">العقود</h1>
            <p className="mt-2 max-w-3xl text-sm leading-7 text-slate-600">
              تابعي اتفاقيات جمعيتك مع منصة سياق: التوقيع، متابعة الحالة، وتنزيل النسخة النهائية.
            </p>
          </div>
          <button
            type="button"
            onClick={load}
            className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            data-testid="association-contracts-refresh"
          >
            <RefreshCw className="h-4 w-4" /> تحديث
          </button>
        </div>

        <StatsRow stats={stats} />

        {loading ? (
          <div className="grid place-items-center py-24" data-testid="association-contracts-loading">
            <Loader2 className="h-8 w-8 animate-spin text-teal-700" />
          </div>
        ) : documents.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            {current.length > 0 && (
              <Section title="الاتفاقية الحالية">
                <div className="grid gap-3 md:grid-cols-2">
                  {current.map((doc) => (
                    <ContractCard key={doc.id} doc={doc} onOpen={() => setOpenId(doc.id)} />
                  ))}
                </div>
              </Section>
            )}
            {history.length > 0 && (
              <Section title="السجل السابق">
                <div className="grid gap-3 md:grid-cols-2">
                  {history.map((doc) => (
                    <ContractCard key={doc.id} doc={doc} onOpen={() => setOpenId(doc.id)} />
                  ))}
                </div>
              </Section>
            )}
          </>
        )}

        {openId && (
          <ContractDetailDrawer
            documentId={openId}
            currentUser={user}
            onClose={closeDrawer}
            onChanged={load}
          />
        )}
      </div>
    </DashboardLayout>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-6">
      <h2 className="mb-3 font-heading text-lg font-bold text-slate-900">{title}</h2>
      {children}
    </section>
  );
}

function StatsRow({ stats }) {
  const cards = [
    { key: "mySign", label: "مطلوب مني", value: stats.mySign, tone: "bg-amber-50 text-amber-800 border-amber-200" },
    { key: "waitingSiaq", label: "قيد الإقفال", value: stats.waitingSiaq, tone: "bg-sky-50 text-sky-700 border-sky-200" },
    { key: "active", label: "سارية", value: stats.active, tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    { key: "closed", label: "منتهية أو ملغاة", value: stats.closed, tone: "bg-slate-100 text-slate-600 border-slate-200" },
  ];
  return (
    <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {cards.map((card) => (
        <div key={card.key} className={`rounded-2xl border p-4 ${card.tone}`}>
          <div className="text-xs font-semibold">{card.label}</div>
          <div className="mt-1 text-2xl font-extrabold">{card.value}</div>
        </div>
      ))}
    </div>
  );
}

function ContractCard({ doc, onOpen }) {
  return (
    <article
      className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
      data-testid={`association-contract-card-${doc.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs font-semibold text-slate-500">{KIND_LABEL[doc.kind] || doc.kind}</div>
          <div className="mt-0.5 font-bold text-slate-900 line-clamp-1">
            {doc.title || "اتفاقية بلا عنوان"}
          </div>
          <div className="mt-1 font-mono text-xs text-slate-500" dir="ltr">
            {doc.number || doc.id.slice(-8)}
          </div>
        </div>
        <StateBadge state={doc.state} />
      </div>
      <div className="mt-3 grid gap-2 text-xs text-slate-600 sm:grid-cols-2">
        <div>الإصدار: <span className="font-semibold text-slate-800">{doc.template_version ?? 1}</span></div>
        <div>سريان من: <span className="font-semibold text-slate-800">{formatShortDate(doc.valid_from)}</span></div>
        <div>سريان إلى: <span className="font-semibold text-slate-800">{formatShortDate(doc.valid_until)}</span></div>
        <div>أُنشئت: <span className="font-semibold text-slate-800">{formatShortDate(doc.created_at)}</span></div>
      </div>
      <div className="mt-4 flex justify-end">
        <button
          type="button"
          onClick={onOpen}
          className="inline-flex min-h-11 items-center gap-1.5 rounded-xl bg-slate-900 px-4 text-xs font-bold text-white hover:bg-slate-800"
          data-testid={`association-contract-open-${doc.id}`}
        >
          فتح التفاصيل
          <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </article>
  );
}

function EmptyState() {
  return (
    <div className="grid place-items-center rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center">
      <FileSignature className="h-10 w-10 text-slate-300" />
      <div className="mt-3 font-bold text-slate-800">لا توجد اتفاقيات صادرة بعد</div>
      <div className="mt-1 max-w-md text-xs leading-6 text-slate-500">
        عند إصدار إدارة منصة سياق اتفاقية جديدة لجمعيتك، ستصلك تنبيه على البريد،
        وستظهر هنا لمراجعتها وتوقيعها.
      </div>
    </div>
  );
}

function ContractDetailDrawer({ documentId, currentUser, onClose, onChanged }) {
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/contracts/documents/${documentId}`);
      setDocument(data.document);
      setNotFound(false);
    } catch (error) {
      const code = error?.response?.status;
      if (code === 404 || code === 403) {
        setNotFound(true);
      } else {
        toast.error(formatApiError(error));
      }
    } finally {
      setLoading(false);
    }
  }, [documentId]);

  useEffect(() => {
    load();
  }, [load]);

  const ownSig = useMemo(
    () => (document?.signatures || []).find((s) => s.slot === "association") || null,
    [document],
  );
  const siaqSig = useMemo(
    () => (document?.signatures || []).find((s) => s.slot === "siaq") || null,
    [document],
  );

  const canSignAsAssociation =
    document?.my_signer_slot === "association"
      && document.state === "PENDING_SIGNATURES"
      && !ownSig;

  const showWaitingSiaq =
    document?.my_signer_slot === "association"
      && ownSig
      && !siaqSig
      && ["PARTIALLY_SIGNED", "FINALIZING"].includes(document.state);

  const showAdminProcessing =
    ["FINALIZING", "FINALIZATION_FAILED"].includes(document?.state || "");

  return (
    <div
      className="fixed inset-0 z-40 flex justify-start bg-slate-900/60 backdrop-blur-sm"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <aside
        className="flex h-full w-full max-w-3xl flex-col overflow-hidden bg-white shadow-2xl"
        dir="rtl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="association-contract-dialog-title"
        tabIndex={-1}
        data-testid="association-contract-detail"
      >
        <header className="flex items-start justify-between gap-3 border-b border-slate-200 px-4 py-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-teal-700">
              {document ? KIND_LABEL[document.kind] : "..."}
            </div>
            <h2 id="association-contract-dialog-title" className="mt-1 font-heading text-xl font-extrabold text-slate-900">
              {document?.title || "تفاصيل العقد"}
            </h2>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
              <span dir="ltr" className="font-mono">{document?.number || documentId}</span>
              {document?.state && <StateBadge state={document.state} />}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 hover:bg-slate-100"
            aria-label="إغلاق"
            data-testid="association-contract-close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          {loading ? (
            <div className="grid place-items-center py-24">
              <Loader2 className="h-8 w-8 animate-spin text-teal-700" />
            </div>
          ) : notFound ? (
            <div className="grid place-items-center py-20 text-center">
              <FileSignature className="h-10 w-10 text-slate-300" />
              <div className="mt-3 font-bold text-slate-800">تعذّر الوصول للعقد</div>
              <div className="mt-1 max-w-sm text-xs leading-6 text-slate-500">
                إما أن الرابط غير صحيح أو أن هذا العقد لا يخص جمعيتك.
              </div>
            </div>
          ) : document ? (
            <div className="space-y-5">
              <div className="grid gap-3 sm:grid-cols-3">
                <MetaCell label="أُنشئت في" value={formatDateTime(document.created_at)} />
                <MetaCell label="سريان من" value={formatShortDate(document.valid_from)} />
                <MetaCell label="سريان إلى" value={formatShortDate(document.valid_until)} />
              </div>

              {document.state === "DRAFT" && (
                <AssociationAgreementDraftEditor
                  document={document}
                  onChanged={async () => {
                    await load();
                    onChanged?.();
                  }}
                />
              )}

              <div className="rounded-2xl border border-slate-200 bg-white">
                <div className="border-b border-slate-200 px-4 py-3 text-sm font-bold text-slate-800">
                  التوقيعات
                </div>
                <div className="divide-y divide-slate-100">
                  {(document.signatures || []).length === 0 && (
                    <div className="p-4 text-sm text-slate-500">لا توجد توقيعات بعد.</div>
                  )}
                  {(document.signatures || []).map((sig) => (
                    <div key={sig.id} className="flex items-center justify-between gap-3 px-4 py-3">
                      <div>
                        <div className="text-sm font-bold text-slate-900">
                          {sig.slot === "siaq"
                            ? "منصة سياق"
                            : sig.slot === "association"
                            ? "الجمعية"
                            : "الطالب"}
                        </div>
                        <div className="text-xs text-slate-500">{sig.typed_name || "—"}</div>
                      </div>
                      <div className="text-xs text-slate-500">{formatDateTime(sig.signed_at)}</div>
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
                        <CheckCircle2 className="h-3.5 w-3.5" /> موثّق
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="mb-2 text-sm font-bold text-slate-800">نص الوثيقة الكامل</div>
                <div
                  className="max-h-[52vh] overflow-y-auto whitespace-pre-wrap rounded-xl bg-slate-50 p-4 text-sm leading-8 text-slate-800"
                  dir="rtl"
                  data-testid="association-contract-text"
                >
                  {document.rendered_text || document.legal_text || "—"}
                </div>
              </div>

              {showWaitingSiaq && (
                <div
                  className="flex items-start gap-3 rounded-2xl border border-sky-200 bg-sky-50 p-4"
                  data-testid="association-contract-waiting-siaq"
                >
                  <Info className="mt-0.5 h-5 w-5 shrink-0 text-sky-600" />
                  <div className="text-sm leading-7 text-sky-900">
                    <div className="font-bold">تم توقيعك</div>
                    <div className="mt-1">
                      الاتفاقية الآن قيد الإقفال النهائي. سنُنبّهك تلقائيًا عند اكتمالها
                      وستصبح جاهزة للتنزيل بصيغة PDF.
                    </div>
                  </div>
                </div>
              )}

              {showAdminProcessing && !canSignAsAssociation && (
                <div className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <Info className="mt-0.5 h-5 w-5 shrink-0 text-slate-500" />
                  <div className="text-sm leading-7 text-slate-700">
                    {document.state === "FINALIZING"
                      ? "المعالجة النهائية جارية لدى إدارة منصة سياق. سيصلك إشعار فور اكتمالها."
                      : "تعذّرت المعالجة النهائية لدى إدارة المنصة. تتم متابعة الأمر داخليًا وسنعلمك عند إعادة الإقفال."}
                  </div>
                </div>
              )}

              {canSignAsAssociation && (
                <ContractSignaturePanel
                  documentId={documentId}
                  expectedName={currentUser?.name}
                  onSigned={async () => {
                    await load();
                    onChanged?.();
                  }}
                />
              )}

              {document.artifact_file_id && (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-bold text-emerald-800">تنزيل النسخة النهائية</div>
                      <div className="mt-1 text-xs text-emerald-700">
                        الملف مُخزّن في المخزن الآمن ويُخدَّم عبر رابط موقّع قصير الصلاحية.
                      </div>
                    </div>
                    <SignedLink
                      fileId={document.artifact_file_id}
                      filename={`${document.number}.pdf`}
                      className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-emerald-700 px-4 text-sm font-bold text-white hover:bg-emerald-600"
                      testid="association-contract-download"
                    >
                      <Download className="h-4 w-4" />
                      تنزيل PDF
                    </SignedLink>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </aside>
    </div>
  );
}

const DRAFT_TEXT_FIELDS = [
  { key: "project_name", label: "اسم المشروع أو البرنامج", placeholder: "برنامج التدريب التعاوني" },
  { key: "association_license_number", label: "رقم ترخيص الجمعية", placeholder: "رقم الترخيص الرسمي" },
  { key: "association_address", label: "عنوان الجمعية", placeholder: "المدينة، الحي، العنوان" },
  { key: "association_representative_name", label: "اسم ممثل الجمعية", placeholder: "الاسم الكامل للممثل المفوض" },
  { key: "association_representative_title", label: "المسمى الوظيفي للممثل", placeholder: "مثال: المدير التنفيذي" },
];
const DRAFT_DATE_FIELDS = [
  { key: "agreement_signing_date", label: "تاريخ الاتفاقية" },
  { key: "agreement_start_date", label: "بداية سريان الاتفاقية" },
  { key: "agreement_end_date", label: "نهاية سريان الاتفاقية" },
];

function AssociationAgreementDraftEditor({ document, onChanged }) {
  const [values, setValues] = useState(() => ({ ...(document.variables || {}) }));
  const [busy, setBusy] = useState("");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setValues({ ...(document.variables || {}) }); }, [document.id, document.revision]);
  const update = (key, value) => setValues(current => ({ ...current, [key]: value }));
  const validate = () => {
    for (const field of [...DRAFT_TEXT_FIELDS, ...DRAFT_DATE_FIELDS]) {
      if (!String(values[field.key] || "").trim()) return `${field.label} مطلوب`;
    }
    if (new Date(values.agreement_end_date) < new Date(values.agreement_start_date)) return "نهاية السريان يجب أن تكون بعد البداية";
    return "";
  };
  const save = async () => {
    const error = validate(); if (error) return toast.error(error);
    setBusy("save");
    try {
      await api.put(`/contracts/documents/${document.id}/association-draft`, { values });
      toast.success("تم حفظ بيانات الاتفاقية");
      await onChanged?.();
    } catch (requestError) { toast.error(formatApiError(requestError)); }
    finally { setBusy(""); }
  };
  const preview = async () => {
    const error = validate(); if (error) return toast.error(error);
    setBusy("preview"); let objectUrl = "";
    try {
      const { data } = await api.post(`/contracts/documents/${document.id}/association-preview`, { values }, { responseType: "blob" });
      objectUrl = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const popup = window.open(objectUrl, "_blank", "noopener,noreferrer");
      if (!popup) { const link = window.document.createElement("a"); link.href = objectUrl; link.download = `agreement-${document.number}-preview.pdf`; link.click(); }
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
    } catch (requestError) { if (objectUrl) URL.revokeObjectURL(objectUrl); toast.error(formatApiError(requestError)); }
    finally { setBusy(""); }
  };
  const submit = async () => {
    const error = validate(); if (error) return toast.error(error);
    if (!await appConfirm("بعد اعتماد البيانات ستُجمّد صياغة الاتفاقية وتنتقل إلى خطوة توقيع الجمعية بالرسم ورمز التحقق. هل تريد المتابعة؟", { title: "اعتماد بيانات الاتفاقية", confirmLabel: "اعتماد والانتقال للتوقيع" })) return;
    setBusy("submit");
    try {
      await api.post(`/contracts/documents/${document.id}/association-submit`, { values });
      toast.success("تم اعتماد البيانات؛ أكمل الآن توقيع الاتفاقية");
      await onChanged?.();
    } catch (requestError) { toast.error(formatApiError(requestError)); }
    finally { setBusy(""); }
  };
  return <section className="rounded-2xl border border-violet-200 bg-violet-50/50 p-5" data-testid="association-agreement-draft-editor">
    <div><h3 className="font-heading text-lg font-black text-slate-900">استكمال بيانات اتفاقية الفرصة</h3><p className="mt-1 text-sm leading-6 text-slate-600">راجع البيانات، احفظها، ثم عاين PDF قبل اعتماد الصياغة والانتقال إلى التوقيع الإلكتروني.</p></div>
    <div className="mt-4 grid gap-3 sm:grid-cols-2">
      <label className="text-sm font-bold text-slate-700">اسم الجمعية الرسمي<input value={values.association_name || ""} readOnly className="mt-1 min-h-11 w-full rounded-xl border bg-slate-100 px-3 font-normal text-slate-500" /></label>
      {DRAFT_TEXT_FIELDS.map(field => <label key={field.key} className="text-sm font-bold text-slate-700">{field.label}<input value={values[field.key] || ""} onChange={event => update(field.key, event.target.value)} placeholder={field.placeholder} className="mt-1 min-h-11 w-full rounded-xl border bg-white px-3 font-normal" data-testid={`agreement-draft-${field.key}`} /></label>)}
      {DRAFT_DATE_FIELDS.map(field => <label key={field.key} className="text-sm font-bold text-slate-700">{field.label}<input type="date" value={(values[field.key] || "").slice(0, 10)} onChange={event => update(field.key, event.target.value)} className="mt-1 min-h-11 w-full rounded-xl border bg-white px-3 font-normal" data-testid={`agreement-draft-${field.key}`} /></label>)}
    </div>
    <div className="mt-5 flex flex-wrap justify-end gap-2">
      <button type="button" onClick={save} disabled={Boolean(busy)} className="inline-flex min-h-11 items-center gap-2 rounded-xl border bg-white px-4 text-sm font-bold text-slate-700 disabled:opacity-50" data-testid="agreement-draft-save">{busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}حفظ البيانات</button>
      <button type="button" onClick={preview} disabled={Boolean(busy)} className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-teal-700 bg-white px-4 text-sm font-bold text-teal-800 disabled:opacity-50" data-testid="agreement-draft-preview">{busy === "preview" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}معاينة PDF</button>
      <button type="button" onClick={submit} disabled={Boolean(busy)} className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-teal-700 px-5 text-sm font-bold text-white disabled:opacity-50" data-testid="agreement-draft-submit">{busy === "submit" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}اعتماد والانتقال للتوقيع</button>
    </div>
  </section>;
}

function MetaCell({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="text-xs font-semibold text-slate-500">{label}</div>
      <div className="mt-1 text-sm text-slate-800">{value}</div>
    </div>
  );
}
